import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Search, Edit2, Building2, Download } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { CreateBranchForm } from './CreateBranchForm';

interface Branch {
  id: number;
  name: string;
  code?: string;
  city?: string;
  pincode?: string;
  address?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  isActive: boolean;
  stateId?: number;
  state?: { id: number; name: string };
}

export function BranchesPage() {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/branches/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `branches-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setExporting(false);
    }
  };

  const { data: branches, isLoading } = useQuery({
    queryKey: ['branches', { search }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      const r = await api.get<Branch[]>('/branches', { params });
      return r.data;
    },
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Branches</h1>
          <p className="text-slate-500 text-sm mt-1">Manage hubs, warehouses and operational branches</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2 disabled:opacity-60">
            <Download size={16} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
            <Plus size={18} /> Add Branch
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input type="text" placeholder="Search by name, code, city..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 whitespace-nowrap">Branch</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Code</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">City</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">State</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Pincode</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Contact</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={8} className="text-center py-12 text-slate-400">Loading...</td></tr>)}
              {!isLoading && (branches?.length ?? 0) === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">
                  <Building2 size={24} className="mx-auto mb-2 opacity-50" />
                  No branches yet. Add your first branch to get started.
                </td></tr>
              )}
              {branches?.map((b) => (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {b.code ? <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{b.code}</code> : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm">{b.city ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm">{b.state?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm font-mono">{b.pincode ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm">
                    {b.contactName ? (
                      <div>
                        <div>{b.contactName}</div>
                        {b.contactPhone && <div className="text-xs text-slate-400">{b.contactPhone}</div>}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${b.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${b.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {b.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setEditing(b)}
                      className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-50 rounded" title="Edit">
                      <Edit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Branch" subtitle="Create a new operational branch or hub" maxWidth="2xl">
        <CreateBranchForm onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit Branch" subtitle={editing?.name} maxWidth="2xl">
        {editing && (
          <CreateBranchForm
            branchId={editing.id}
            defaultValues={{
              name: editing.name,
              code: editing.code,
              stateId: editing.stateId ?? '',
              city: editing.city,
              address: editing.address,
              pincode: editing.pincode,
              contactName: editing.contactName,
              contactPhone: editing.contactPhone,
              contactEmail: editing.contactEmail,
              isActive: editing.isActive,
            }}
            onSuccess={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}
